import React, { useEffect, useRef, useState } from 'react'
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import * as THREE from 'three'
import photo from '../../images/photo.svg';
import { TrackballControls } from "three/examples/jsm/controls/experimental/CameraControls";
import { TransformControls } from "three/examples/jsm/controls/TransformControls";
import { Lut } from "three/examples/jsm/math/Lut";
import './Scene.css';
import r from './images/rotare.svg';
import o from './images/on.svg';
import t from './images/translate.svg';
import SceneLegend from "./SceneLegend";
import { setPrintCamera } from "../Store/LoadReducer";
import { useDispatch, useSelector } from "react-redux";
import { formatDecimal, fileStorage } from '../../helpers/utils';



function Scene({ classes, clearColor, legends, thisId, maps, help, orbit }) {
    const mount = useRef(null);
    const viewFFR = useSelector(item => item.flowviewer.viewFFR);
    const printCamera = useSelector(item => item.flowviewer.printCamera);
    const activeVessel = useSelector(item => item.flowviewer.activeVessel);
    const [press, setPress] = useState(false);
    const [view, setView] = useState(false);
    const dispatch = useDispatch();

    let scene;

    useEffect(async () => {
        let width = mount.current.clientWidth;
        let height = mount.current.clientHeight;
        let frameId;

        scene = new THREE.Scene();

        const camera = new THREE.PerspectiveCamera(
            70,
            width / height,
            0.1,
            10000
        );

        help && scene.add(new THREE.AxesHelper(10000));
        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            preserveDrawingBuffer: true
        });
        renderer.outputEncoding = THREE.sRGBEncoding;
        renderer.setClearColor(clearColor);
        renderer.setSize(width, height);

        camera.position.set(100, 100, 100);
        const controls = new TrackballControls(camera, renderer.domElement);
        controls.rotateSpeed = 0.7;
        controls.noZoom = true;
        controls.noPan = true;
        controls.enabled = orbit;

        const control = new TransformControls(camera, renderer.domElement);

        function getLight(color, position, int) {
            const alight = new THREE.PointLight(color, int);
            alight.position.x = position.x;
            alight.position.z = position.y;
            alight.position.y = position.z;
            scene.add(alight);
        }

        getLight(0xffffff, { x: 100, y: 100, z: 100 }, 0.5);
        getLight(0xffffff, { x: -100, y: -100, z: -100 }, 0.5);

        const pointLight = new THREE.AmbientLight(0xffffff, 0.5);
        scene.add(pointLight);

        const lut = new Lut();
        lut.addColorMap("rainbowInvert",
            [
                [0.0, "#960c0c"],
                [0.3, "#960c0c"],
                [0.6, "#f50f0f"],
                [0.7, "#ff3e1c"],
                [0.75, "#fca503"],
                [0.775, "#fcc603"],
                [0.8, "#fffb00"],
                [0.825, "#fcc603"],
                [0.85, "#0dff00"],
                [0.9, "#00ffff"],
                [0.95, "#0000ff"],
                [1, "#0000ff"]
            ]
        );
        lut.setColorMap('rainbowInvert');

        const loader = new GLTFLoader();
        const [coloringJson, stlUrl] = await Promise.all([
            fileStorage.loadFile("coloring").then(response => response.data.text().then(JSON.parse)),
            fileStorage.loadFile("stl").then(response => URL.createObjectURL(new Blob([response.data])))
        ]);
        
        loader.load(stlUrl, (gltf) => {
            const model = gltf.scene;
            scene.add(model);
            
            model.traverse(function(mesh) {
                if (mesh.isMesh) {
                    mesh.geometry.center();
                    mesh.geometry.computeVertexNormals();
                    
                    const count = mesh.geometry.attributes.position.count;
                    const colors = new Float32Array(count * 3);
                    colors.fill(1);
                    mesh.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
                    
                    lut.setMax(1);
                    lut.setMin(0);
                    
                    const colorAttr = mesh.geometry.attributes.color;
                    if (coloringJson) {
                        for (let i = 0; i < count; i++) {
                            const colorValue = coloringJson[i];
                            if (colorValue === -1 || colorValue === undefined) {
                                colorAttr.setXYZ(i, 0.25, 0.25, 0.25);
                            } else {
                                const color = lut.getColor(colorValue);
                                if (color) {
                                    colorAttr.setXYZ(i, color.r, color.g, color.b);
                                } else {
                                    colorAttr.setXYZ(i, 0.25, 0.25, 0.25);
                                }
                            }
                        }
                    }
                    
                    mesh.material = new THREE.MeshPhongMaterial({
                        vertexColors: true
                    });
                    scene.add(mesh);
                    setView(true);
                    
                    // Set camera position
                    if (printCamera[activeVessel]?.position?.x) {
                        camera.position.x = printCamera[activeVessel].position.x;
                        camera.position.y = printCamera[activeVessel].position.y;
                        camera.position.z = printCamera[activeVessel].position.z;
                        camera.rotation.x = printCamera[activeVessel].rotation.x;
                        camera.rotation.y = printCamera[activeVessel].rotation.y;
                        camera.rotation.z = printCamera[activeVessel].rotation.z;
                    } else {
                        camera.position.x = mesh.geometry.boundingBox.max.x - mesh.geometry.boundingBox.min.x;
                        camera.position.y = mesh.geometry.boundingBox.max.y - mesh.geometry.boundingBox.min.y;
                        camera.position.z = mesh.geometry.boundingBox.max.z - mesh.geometry.boundingBox.min.z;
                    }
                }
            });
            
            control.attach(model);
            control.setMode("rotate");
            control.setSpace('world');
            control.enabled = false;
            control.visible = false;
            scene.add(control);
        }, 
        (xhr) => {
            if (!view) {
                const text = Math.round(xhr.loaded / xhr.total * 100);
                if (text === 100) {
                    setView(true);
                } else {
                    const progressBarElement = document.getElementById("progress");
                    if (progressBarElement) {
                        progressBarElement.innerText = text + "%";
                        progressBarElement.setAttribute("aria-valuenow", text + "%");
                        progressBarElement.setAttribute("style", "width: " + text + "%");
                    }
                }
            }
        },
        (err) => {
            // TODO: log error
        });

        control.addEventListener('dragging-changed', function (event) {
            controls.enabled = !event.value;
        });

        document.getElementById("on" + thisId).addEventListener('click', function () {
            control.enabled = !control.enabled;
            control.visible = !control.visible;
        });

        document.getElementById("rotare" + thisId).addEventListener('click', function () {
            control.setMode('rotate');
        });

        document.getElementById("translate" + thisId).addEventListener('click', function () {
            control.setMode('translate');
        });

        document.getElementById("saveToJpg").addEventListener('click', function () {
            const cam = {
                position: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
                rotation: { x: camera.rotation.x, y: camera.rotation.y, z: camera.rotation.z }
            };
            setPress(true);
            renderer.setClearColor("white");
            setTimeout(() => {
                setPress(false);
                renderer.setClearColor(clearColor);
            }, 250);
            dispatch(setPrintCamera({ data: cam }));
        });

        const renderScene = () => {
            renderer.render(scene, camera)
        }

        const handleResize = () => {
            width = mount.current.clientWidth;
            height = mount.current.clientHeight;
            renderer.setSize(width, height);
            camera.aspect = width / height;
            camera.updateProjectionMatrix();
            renderScene()
        }

        const animate = () => {
            renderScene();
            camera.updateMatrixWorld();
            frameId = window.requestAnimationFrame(animate);
            controls.update();
        };

        const start = () => {
            if (!frameId) {
                frameId = requestAnimationFrame(animate);
            }
        };

        const stop = () => {
            cancelAnimationFrame(frameId);
            frameId = null
        };

        mount.current.appendChild(renderer.domElement);
        window.addEventListener('resize', handleResize);
        start();

        return () => {
            stop();
            window.removeEventListener('resize', handleResize);
        };
    }, []);


    const [onb, setOnb] = useState(false);
    const [onr, setOnr] = useState(true);
    const [ont, setOnt] = useState(false);

    function setIn(ins) {
        if (ins.in === r) {
            setOnr(true);
            setOnt(false);
        } else {
            setOnr(false);
            setOnt(true);
        }
    }

    return (
        <div id="sceneParent" className={"scene " + classes}
            ref={mount}>
            <button id="saveToJpg" className={press ? "toogle-control photo active" : "toogle-control photo"}>
                <img className="w-100" src={photo} alt="" />
            </button>
            {
                !view &&
                <div className="progress progress-bars">
                    <div id="progress" className="progress-bar progress-bar-striped" role="progressbar"
                        aria-valuenow="25"
                        aria-valuemin="0" aria-valuemax="100">
                        0%
                    </div>
                </div>
            }
            <div className="buttons">
                <button id={"on" + thisId} onClick={() => setOnb(!onb)}
                    className={onb ? "toogle-control active" : "toogle-control"}>
                    <img src={o} alt="" className="w-100" />
                </button>
                <div className={onb ? "" : "d-none"}>
                    <button id={"rotare" + thisId} onClick={() => setIn({ in: r })}
                        className={onr ? "toogle-control active" : "toogle-control"}>
                        <img src={r} alt="" className="w-100" />
                    </button>
                    <button id={"translate" + thisId} onClick={() => setIn({ in: t })}
                        className={ont ? "toogle-control active" : "toogle-control"}>
                        <img src={t} alt="" className="w-100" />
                    </button>
                </div>
                <div className="mb-0 text-white">
                    {
                        Object.keys(viewFFR).map((vessel) => (
                            viewFFR[vessel].ffr ?
                                <div key={`scene-text-label-${vessel}`} className="scene-text-label">{vessel + " FFR: " + formatDecimal(viewFFR[vessel].ffr)} </div> : ""
                        ))
                    }
                </div>
            </div>
            <SceneLegend maps={maps} legends={legends} type={"ffr"} />
        </div>
    );
}

export default Scene;